'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, MotionConfig } from 'framer-motion';
import {
  AlertCircle,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Instagram,
  Linkedin,
  Loader2,
  Mail,
  Phone,
  Send,
} from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { LandingSilk } from '@/components/landing/LandingSilk';
import { useI18n } from '@/contexts/I18nContext';

const fieldClassName =
  'w-full rounded-2xl bg-white/[0.075] px-4 py-3.5 text-sm text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16),inset_0_-1px_0_rgba(255,255,255,0.05)] outline-none transition-[background-color,box-shadow] duration-300 placeholder:text-white/30 hover:bg-white/[0.1] focus:bg-white/[0.13] focus:shadow-[inset_0_1px_0_rgba(255,255,255,0.24),0_0_0_2px_rgba(182,156,255,0.55)]';

type SubmitStatus = 'idle' | 'success' | 'error';

export default function ContactPage() {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');

  const setField = (key: keyof typeof formData, value: string) => {
    setFormData((current) => ({ ...current, [key]: value }));
    if (submitStatus !== 'idle') setSubmitStatus('idle');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/support/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to send contact request');

      setFormData({ name: '', email: '', subject: '', message: '' });
      setSubmitStatus('success');
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactItems = [
    {
      icon: Mail,
      label: t('contact.info.email'),
      value: 'info@cannect.ai',
      href: 'mailto:info@cannect.ai',
    },
    {
      icon: Phone,
      label: t('contact.info.phone'),
      value: t('common.companyPhone'),
      href: 'tel:+77758221235',
    },
    {
      icon: Instagram,
      label: 'Instagram',
      value: '@cannect.ai',
      href: 'https://www.instagram.com/cannect.ai',
      external: true,
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: 'CANNECT.AI',
      href: 'https://www.linkedin.com/company/cannect-ai',
      external: true,
    },
  ];

  const legalLinks = [
    { label: t('common.offer'), href: '/offer' },
    { label: t('common.terms'), href: '/terms' },
    { label: t('common.privacy'), href: '/privacy' },
    { label: t('common.payment'), href: '/payment' },
    { label: t('common.refund'), href: '/refund' },
  ];

  return (
    <MotionConfig reducedMotion="user">
      <main id="new-contact-page" className="new-landing-shell relative min-h-screen overflow-hidden bg-[#09031a] text-white">
        {/* Единый брендовый фон — тот же WebGL-silk, что на главной. */}
        <LandingSilk
          baseColor="#28137F"
          highlightColor="#4144E6"
          className="pointer-events-none fixed inset-0 z-0"
        />
        <div className="fixed inset-0 z-0 bg-black/35" />
        <div className="fixed inset-0 z-0 bg-gradient-to-b from-black/10 via-transparent to-black/65" />

        <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1500px] flex-col p-3 sm:p-5 lg:p-6">
          <motion.header
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mb-3 flex items-center justify-between sm:mb-5"
          >
            <Link href="/new-landing" aria-label={t('contact.backHome')} className="transition-transform hover:scale-[1.02]">
              <Logo className="h-auto w-44 text-white sm:w-56" color="currentColor" />
            </Link>
            <Link
              href="/new-landing"
              className="liquid-glass inline-flex h-11 items-center gap-2 rounded-full px-4 text-xs font-medium text-white/75 transition-transform hover:scale-105 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t('contact.backHome')}</span>
            </Link>
          </motion.header>

          <div className="grid flex-1 gap-3 lg:grid-cols-[0.82fr_1.18fr] lg:gap-4">
            <motion.section
              initial={{ opacity: 0, x: -28, scale: 0.985 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.72, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="liquid-glass-strong flex min-h-0 flex-col rounded-[2rem] p-5 sm:p-7 lg:p-7 xl:p-8"
            >
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-white/45">
                  {t('contact.eyebrow')}
                </p>
                <h1 className="mt-5 max-w-xl text-4xl leading-[1.02] tracking-[-0.045em] sm:text-5xl lg:text-[3.5rem]">
                  {t('contact.newTitleLead')}{' '}
                  <em className="text-brand-100/90">{t('contact.newTitleAccent')}</em>
                </h1>
                <p className="mt-5 max-w-lg text-sm leading-6 text-white/58 sm:text-base sm:leading-7">
                  {t('contact.newDescription')}
                </p>
              </div>

              <div className="mt-8 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {contactItems.map(({ icon: Icon, label, value, href, external }) => (
                  <a
                    key={label}
                    href={href}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noreferrer' : undefined}
                    className="liquid-glass group relative flex min-h-[76px] items-center gap-2.5 rounded-2xl p-3 pr-7 transition-transform hover:scale-[1.02]"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-500/25 text-brand-100">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[9px] uppercase tracking-[0.18em] text-white/35">{label}</span>
                      <span className="mt-1 block text-[13px] font-medium leading-4 text-white/85">{value}</span>
                    </span>
                    <ArrowUpRight className="absolute right-3 top-3 h-3.5 w-3.5 text-white/25 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white/70" />
                  </a>
                ))}
              </div>

              <div className="mt-7 h-px bg-white/12" />

              <div className="mt-5 grid gap-5 sm:grid-cols-[1fr_auto] sm:items-start lg:grid-cols-1 xl:grid-cols-[1fr_auto]">
                <div className="text-xs leading-5 text-white/48">
                  <p className="font-medium text-white/75">{t('common.companyLegalName')}</p>
                  <p className="mt-1">{t('common.companyBin')}</p>
                  <p>{t('common.companyAddressLine1')}</p>
                  <p>{t('common.companyAddressLine2')}</p>
                  {t('common.companyAddressLine3') ? <p>{t('common.companyAddressLine3')}</p> : null}
                </div>

                <div>
                  <p className="text-[9px] uppercase tracking-[0.18em] text-white/35">
                    {t('common.paymentMethods')}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="flex h-9 w-16 items-center justify-center rounded-xl bg-white/90 px-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                      <Image src="/logos/visa.svg" alt="Visa" width={48} height={16} unoptimized className="h-auto w-full" />
                    </span>
                    <span className="flex h-9 w-14 items-center justify-center rounded-xl bg-white/90 px-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                      <Image src="/logos/mastercard.svg" alt="Mastercard" width={32} height={20} unoptimized className="h-5 w-auto" />
                    </span>
                  </div>
                </div>
              </div>

              <nav aria-label={t('common.terms')} className="mt-5 flex flex-wrap gap-x-4 gap-y-2">
                {legalLinks.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="text-[10px] text-white/42 transition-colors hover:text-white/85"
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

            </motion.section>

            <motion.section
              initial={{ opacity: 0, x: 28, scale: 0.985 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.72, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="liquid-glass-strong flex flex-col rounded-[2rem] p-5 sm:p-7 lg:p-7 xl:p-8"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-white/40">
                    {t('contact.form.eyebrow')}
                  </p>
                  <h2 className="mt-3 text-3xl tracking-[-0.035em] sm:text-4xl">
                    {t('contact.form.titleLead')}{' '}
                    <em className="text-brand-100/85">{t('contact.form.titleAccent')}</em>
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-white/50">{t('contact.form.description')}</p>
                </div>
                <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-500/25 text-brand-100 sm:flex">
                  <Send className="h-4 w-4" />
                </span>
              </div>

              <form onSubmit={handleSubmit} className="mt-5 flex flex-1 flex-col gap-3.5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-[10px] uppercase tracking-[0.16em] text-white/42">{t('support.yourName')}</span>
                    <input
                      type="text"
                      autoComplete="name"
                      value={formData.name}
                      onChange={(event) => setField('name', event.target.value)}
                      placeholder={t('contact.form.namePlaceholder')}
                      className={fieldClassName}
                      required
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-[10px] uppercase tracking-[0.16em] text-white/42">{t('common.email')}</span>
                    <input
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={(event) => setField('email', event.target.value)}
                      placeholder="name@company.com"
                      className={fieldClassName}
                      required
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-2 block text-[10px] uppercase tracking-[0.16em] text-white/42">{t('support.subject')}</span>
                  <select
                    value={formData.subject}
                    onChange={(event) => setField('subject', event.target.value)}
                    className={`${fieldClassName} appearance-none ${formData.subject ? 'text-white' : 'text-white/35'}`}
                    required
                  >
                    <option className="bg-[#211039] text-white" value="" disabled>{t('common.selectTopic')}</option>
                    <option className="bg-[#211039] text-white" value="general">{t('support.general')}</option>
                    <option className="bg-[#211039] text-white" value="partnership">{t('contact.form.partnership')}</option>
                    <option className="bg-[#211039] text-white" value="campaign">{t('support.campaign')}</option>
                    <option className="bg-[#211039] text-white" value="technical">{t('support.technical')}</option>
                    <option className="bg-[#211039] text-white" value="other">{t('support.other')}</option>
                  </select>
                </label>

                <label className="flex min-h-[130px] flex-1 flex-col">
                  <span className="mb-2 block text-[10px] uppercase tracking-[0.16em] text-white/42">{t('contact.form.message')}</span>
                  <textarea
                    value={formData.message}
                    onChange={(event) => setField('message', event.target.value)}
                    placeholder={t('support.describe')}
                    className={`${fieldClassName} min-h-[130px] flex-1 resize-none leading-6`}
                    required
                  />
                </label>

                {submitStatus === 'success' ? (
                  <div role="status" className="flex items-center gap-2 rounded-2xl bg-emerald-400/12 px-4 py-3 text-sm text-emerald-100 shadow-[inset_0_0_0_1px_rgba(110,231,183,0.2)]">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {t('support.success')}
                  </div>
                ) : null}

                {submitStatus === 'error' ? (
                  <div role="alert" className="flex items-center gap-2 rounded-2xl bg-coral-400/12 px-4 py-3 text-sm text-coral-100 shadow-[inset_0_0_0_1px_rgba(255,155,134,0.22)]">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {t('support.error')}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-1 inline-flex min-h-12 w-full items-center justify-center gap-3 rounded-full bg-[linear-gradient(110deg,#7452ff,#4f27ed)] px-6 text-sm font-medium text-white shadow-[0_14px_34px_rgba(73,38,220,0.32),inset_0_1px_0_rgba(255,255,255,0.35)] transition-transform hover:scale-[1.015] disabled:cursor-wait disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {isSubmitting ? t('common.sending') : t('support.sendMessage')}
                </button>
              </form>
            </motion.section>
          </div>
        </div>
      </main>
    </MotionConfig>
  );
}
